import { useState, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { PersonalizationVariable } from '@/lib/types/personalization-variable';

interface AutocompleteMatch {
  startPos: number;
  endPos: number;
  query: string;
}

interface AutocompleteState {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  query: string;
  match: AutocompleteMatch | null;
}

/**
 * Finds a variable pattern ({, {{, or {{text) ending at the cursor position.
 * Returns null if cursor is inside a completed placeholder like {{firstName}}
 */
function findVariablePattern(editor: Editor): AutocompleteMatch | null {
  const { from } = editor.state.selection;
  const doc = editor.state.doc;
  const docSize = doc.content.size;

  // Check if we're inside a completed placeholder by looking for }} after cursor
  const textAfter = doc.textBetween(from, Math.min(docSize, from + 30), '');
  if (/^[a-zA-Z]*\}\}/.test(textAfter)) {
    return null;
  }

  // Walk backwards to find the pattern
  let pos = from;
  let query = '';
  let bracketCount = 0;

  // First, collect any letters (the query part)
  while (pos > 1) {
    const char = doc.textBetween(pos - 1, pos, '');
    if (/^[a-zA-Z]$/.test(char)) {
      query = char + query;
      pos--;
    } else {
      break;
    }
  }

  // Then, collect the opening braces
  while (pos > 1) {
    const char = doc.textBetween(pos - 1, pos, '');
    if (char === '{') {
      bracketCount++;
      pos--;
    } else {
      break;
    }
  }

  // Must have at least two opening braces (single { is an AI instruction, not a variable)
  if (bracketCount < 2) {
    return null;
  }

  return {
    startPos: pos,
    endPos: from,
    query,
  };
}

export function useVariableAutocomplete() {
  const [state, setState] = useState<AutocompleteState>({
    isOpen: false,
    position: null,
    query: '',
    match: null,
  });

  const checkForAutocomplete = useCallback((editor: Editor | null) => {
    if (!editor) {
      return;
    }

    const match = findVariablePattern(editor);

    if (match) {
      const coords = editor.view.coordsAtPos(match.startPos);
      const editorContainer = editor.view.dom.closest('[class*="border-input"]') as HTMLElement;

      if (editorContainer) {
        const containerRect = editorContainer.getBoundingClientRect();
        setState({
          isOpen: true,
          position: {
            x: coords.left - containerRect.left,
            y: coords.bottom - containerRect.top + 4,
          },
          query: match.query,
          match,
        });
      }
    } else {
      setState((prev) => {
        if (prev.isOpen) {
          return { isOpen: false, position: null, query: '', match: null };
        }
        return prev;
      });
    }
  }, []);

  const insertVariable = useCallback((editor: Editor | null, variable: PersonalizationVariable) => {
    if (!editor || !state.match) {
      return;
    }

    const { startPos, endPos } = state.match;

    // Use a transaction to delete and insert in one atomic operation
    const tr = editor.state.tr;
    tr.delete(startPos, endPos);
    tr.insertText(variable.placeholder, startPos);
    editor.view.dispatch(tr);
    editor.commands.focus();

    // Close autocomplete
    setState({ isOpen: false, position: null, query: '', match: null });
  }, [state.match]);

  const closeAutocomplete = useCallback(() => {
    setState({ isOpen: false, position: null, query: '', match: null });
  }, []);

  return {
    isOpen: state.isOpen,
    position: state.position,
    query: state.query,
    checkForAutocomplete,
    insertVariable,
    closeAutocomplete,
  };
}
